import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { message } from "antd";
import {
  ProductScreen, Appbar, Summary, EmptyState, Sheet, DetailRow, Field, SelectInput,
  SwitchRow, Pill, Mono, Ico, fmt, useMobileMenu,
} from "../produits/productMobileKit";
import { auditDate, listAuditSessions } from "./auditMobileApi";
import useAuditScope from "../../gestion-audit/useAuditScope";
import {
  createAuditRequestGate,
  isAuditRequestCanceled,
} from "../../gestion-audit/auditRequestGate";

const windowOptions = [{ value: "5", label: "5 minutes" }, { value: "15", label: "15 minutes" }, { value: "30", label: "30 minutes" }, { value: "60", label: "1 heure" }, { value: "120", label: "2 heures" }];
const intervalOptions = [{ value: "60000", label: "1 minute" }, { value: "120000", label: "2 minutes" }, { value: "300000", label: "5 minutes" }];
const isSessionSuperAdmin = (row) => row.is_superadmin === true || row.is_superadmin === 1 || row.is_superadmin === "1";
const idleMeta = (seconds) => {
  const value = Number(seconds || 0);
  if (value < 60) return ["En ligne", "ok"];
  if (value < 300) return [`Inactif ${Math.max(1, Math.floor(value / 60))} min`, "warn"];
  return [`Inactif ${Math.floor(value / 60)} min`, "off"];
};

export default function AuditSessionsMobile() {
  const onMenu = useMobileMenu();
  const {
    auditScope,
    companyId,
    isSuperAdmin,
    scope: effectiveAuditScope,
    scopeKey: auditScopeKey,
    setAuditScope,
  } = useAuditScope();
  const [rows, setRows] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [windowMinutes, setWindowMinutes] = useState("15");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState("120000");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const latestAuditScopeKeyRef = useRef(auditScopeKey);
  useLayoutEffect(() => { latestAuditScopeKeyRef.current = auditScopeKey; }, [auditScopeKey]);
  const previousAuditScopeKeyRef = useRef(auditScopeKey);
  const requestGateRef = useRef(null);
  if (!requestGateRef.current) requestGateRef.current = createAuditRequestGate();

  const load = useCallback(async (silent = false) => {
    const request = requestGateRef.current.start(auditScopeKey);
    if (!silent) setLoading(true);
    try {
      const data = await listAuditSessions(Number(windowMinutes), companyId, {
        scope: effectiveAuditScope,
        signal: request.signal,
      });
      if (!request.isCurrent(latestAuditScopeKeyRef.current)) return;
      setRows(data.sessions || []);
      setGeneratedAt(data.generated_at || new Date().toISOString());
    } catch (error) {
      if (isAuditRequestCanceled(error, request.signal) || !request.isCurrent(latestAuditScopeKeyRef.current)) return;
      if (!silent) message.error(error.message || "Impossible de charger les sessions actives");
    } finally {
      const isCurrent = request.isCurrent(latestAuditScopeKeyRef.current);
      request.finish();
      if (isCurrent) setLoading(false);
    }
  }, [auditScopeKey, companyId, effectiveAuditScope, windowMinutes]);
  useEffect(() => {
    const scopeChanged = previousAuditScopeKeyRef.current !== auditScopeKey;
    previousAuditScopeKeyRef.current = auditScopeKey;
    if (scopeChanged) {
      requestGateRef.current.cancel();
      setRows([]);
      setGeneratedAt(null);
      setDetail(null);
    }
    load();
  }, [auditScopeKey, load]);
  useEffect(() => () => requestGateRef.current?.cancel(), []);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = window.setInterval(() => load(true), Math.max(60000, Number(refreshInterval) || 120000));
    return () => window.clearInterval(timer);
  }, [autoRefresh, load, refreshInterval]);

  const stats = useMemo(() => ({ online: rows.filter((row) => Number(row.idle_seconds || 0) < 60).length, superadmins: rows.filter(isSessionSuperAdmin).length }), [rows]);

  return <ProductScreen>
    <Appbar title="Sessions actives" subtitle={generatedAt ? `Mis à jour à ${new Date(generatedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : "Activité en temps réel"} onMenu={onMenu} action={<><button className="iconbtn" aria-label="Réglages" onClick={() => setSettingsOpen(true)}><Ico name="sliders-horizontal" /></button><button className="iconbtn" aria-label="Actualiser" onClick={() => load()}><Ico name="refresh-cw" /></button></>} />
    <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}><Summary items={[{ label: "Sessions", value: fmt(rows.length) }, { label: "En ligne", value: fmt(stats.online) }, { label: "SuperAdmins", value: fmt(stats.superadmins) }]} /></div>
    <div className="body">{loading && !rows.length ? <EmptyState loading title="Chargement des sessions…" /> : !rows.length ? <EmptyState icon="users" title="Aucune session active" text={`Aucune activité durant les ${windowMinutes} dernières minutes.`} /> : <div className="list">{rows.map((row) => { const meta = idleMeta(row.idle_seconds); return <button className="rcard" key={row.user_id} onClick={() => setDetail(row)}><Mono name={row.username || row.email || "U"} square /><div className="rcard__main"><div className="rcard__name">{row.username || row.email}</div><div className="rcard__meta"><Pill tone={meta[1]} label={meta[0]} />{isSessionSuperAdmin(row) ? <Pill tone="warn" label="SuperAdmin" /> : null}<span className="rcard__sub">{row.company_name || "Sans entreprise"}</span></div></div><span className="rcard__chev"><Ico name="chevron-right" /></span></button>; })}</div>}</div>

    <Sheet open={settingsOpen} onClose={() => setSettingsOpen(false)} title="Réglages des sessions">{isSuperAdmin ? <Field label="Périmètre"><SelectInput value={auditScope} onChange={(scope) => setAuditScope(scope || "company")} options={[{ value: "company", label: "Entreprise sélectionnée" }, { value: "all", label: "Toutes les entreprises" }]} /></Field> : null}<Field label="Fenêtre d'activité"><SelectInput value={windowMinutes} onChange={setWindowMinutes} options={windowOptions} /></Field><SwitchRow label="Actualisation automatique" value={autoRefresh} onChange={setAutoRefresh} />{autoRefresh ? <Field label="Fréquence"><SelectInput value={refreshInterval} onChange={setRefreshInterval} options={intervalOptions} /></Field> : null}<div className="sheet__actions"><button className="btn btn--primary" onClick={() => setSettingsOpen(false)}>Terminer</button></div></Sheet>

    <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.username || detail?.email || "Session"} subtitle={detail?.email}>{detail ? <><DetailRow label="Statut" value={idleMeta(detail.idle_seconds)[0]} /><DetailRow label="Rôle" value={isSessionSuperAdmin(detail) ? "SuperAdmin" : "Utilisateur"} /><DetailRow label="Entreprise" value={detail.company_name || (detail.company_id ? `#${detail.company_id}` : "—")} /><DetailRow label="Dernière activité" value={auditDate(detail.last_activity, true)} /><DetailRow label="Connexion" value={auditDate(detail.login_at, true)} /><DetailRow label="Adresse IP" value={detail.login_ip || "—"} /><DetailRow label="Navigateur" value={detail.login_user_agent || "—"} /></> : null}</Sheet>
  </ProductScreen>;
}
